<template>
  <div class="user-acronym-picker" @keydown.enter.prevent="maybeAddByEnter">
    <v-autocomplete
      ref="ac"
      :items="filteredItems"
      :item-text="itemText"
      :item-value="itemValue"
      :search-input.sync="search"
      :label="label"
      :disabled="disabled"
      menu-props="auto"
      dense
      outlined
      clearable
      hide-details
      :hide-no-data="true"
      :no-filter="true"
      :hide-selected="true"
      return-object
      multiple
      :value="value"
      @input="onInput"
    >

      <template v-slot:selection></template>

      <template v-slot:item="{ item, on, attrs }">
        <div v-bind="attrs" v-on="on" class="px-2 py-2 d-flex align-center">
          <span class="initials mr-2">
            {{ item.initials ? item.initials : (first(item.firstName) + first(item.lastName)) }}
          </span>
          <span>{{ item.fullName || ((item.firstName || '') + ' ' + (item.lastName || '')) }}</span>
        </div>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
export default {
  name: "UserAcronymPicker",
  props: {
    value: { type: Array, default: () => [] },
    items: { type: Array, required: true }, // all users
    label: { type: String, default: "Kürzel oder Name eingeben" },
    itemText: { type: String, default: "fullName" },
    itemValue: { type: String, default: "id" },
    disabled: { type: Boolean, default: false },
    clearOnSelect: { type: Boolean, default: true },
    autoAddOnEnter: { type: Boolean, default: true },
    minChars: { type: Number, default: 1 }, // show results only after N chars
  },
  data() {
    return { search: "" };
  },
  computed: {
    filteredItems() {
      const q = (this.search || "").trim();
      if (q.length < this.minChars) return []; // blank on focus
      const nq = this.norm(q);
      return (this.items || []).filter(u => {
        const name = this.norm(u[this.itemText] || `${u.firstName || ""} ${u.lastName || ""}`);
        if (name.includes(nq)) return true;
        return this.acronyms(u).some(a => this.norm(a).startsWith(nq));
      });
    },
  },
  methods: {
    onInput(newVal) {
      // dedupe by id and bubble up as v-model
      const seen = new Set(), out = [];
      for (const u of newVal || []) {
        const id = u && u[this.itemValue];
        if (!seen.has(id)) { seen.add(id); out.push(u); }
      }
      this.$emit("input", out);
      if (this.clearOnSelect) this.search = "";
    },

    maybeAddByEnter() {
      if (!this.autoAddOnEnter) return;
      const q = (this.search || "").trim();
      if (q.length < this.minChars || this.filteredItems.length === 0) return;

      const nq = this.norm(q);
      const exact = this.filteredItems.find(u =>
        this.acronyms(u).some(a => this.norm(a) === nq)
      );
      const pick = exact || this.filteredItems[0];
      this.onInput([...(this.value || []), pick]);
    },

    acronyms(user) {
      const first = (user.firstName || "").trim();
      const last  = (user.lastName  || "").trim();
      const parts = (user.fullName || `${first} ${last}`).trim().split(/\s+/).filter(Boolean);
      const tokenInitials = parts.map(p => (p[0] || "")).join("").toUpperCase(); // MB
      const f2l1 = (first.slice(0, 2) + (last[0] || "")).toUpperCase();          // MIB
      const f3l1 = (first.slice(0, 3) + (last[0] || "")).toUpperCase();          // MIRB
      const provided = (user.initials || "").toUpperCase();
      const fl = ((first[0] || "") + (last[0] || "")).toUpperCase();
      return Array.from(new Set([tokenInitials, f2l1, f3l1, provided, fl].filter(Boolean)));
    },

    norm(s) {
      return (s || "")
        .toString()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "")
        .toLowerCase();
    },

    first(s) { return (s && s[0] ? s[0] : "").toUpperCase(); },
  },
};
</script>

<style scoped>
.user-acronym-picker .initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #494949;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 500;
}
</style>
